// 微信授权管理器 - 解决偶发授权失败问题
import { isWechatBrowser } from '../config/wechat';
import { buildAuthRedirectUrl } from './authRedirectManager';

// 授权状态管理
const AUTH_STATES = {
  IDLE: 'idle',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILED: 'failed'
};

// 授权锁，防止重复授权
let authLock = false;
let authState = AUTH_STATES.IDLE;
let authPromise = null;

// 授权重试配置
const RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: 1000,
  timeout: 10000
};

// 获取当前授权状态
export const getAuthState = () => {
  return {
    state: authState,
    isLocked: authLock,
    hasPromise: !!authPromise
  };
};

// 设置授权锁
const setAuthLock = (locked) => {
  authLock = locked;
  console.log(`🔒 设置授权锁: ${locked ? '锁定' : '解锁'}`);
};

// 设置授权状态
const setAuthState = (state) => {
  authState = state;
  console.log(`📊 授权状态变更: ${state}`);
};

// 清除授权状态
export const clearAuthState = () => {
  authLock = false;
  authState = AUTH_STATES.IDLE;
  authPromise = null;
  console.log('🧹 清除授权状态');
};

// 检查是否正在处理授权
export const isAuthProcessing = () => {
  return authLock || authState === AUTH_STATES.PROCESSING;
};

// 等待授权完成
export const waitForAuth = async (timeout = 5000) => {
  if (!authPromise) {
    return false;
  }
  
  try {
    await Promise.race([
      authPromise,
      new Promise((_, reject) => 
        setTimeout(() => reject(new Error('授权超时')), timeout)
      )
    ]);
    return true;
  } catch (error) {
    console.error('等待授权失败:', error);
    return false;
  }
};

// 带重试的API请求
const fetchWithRetry = async (url, options, retryCount = 0) => {
  try {
    console.log(`📤 发送请求 (第${retryCount + 1}次):`, url);
    
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), RETRY_CONFIG.timeout);
    
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    console.log(`📥 请求成功 (第${retryCount + 1}次):`, data);
    
    return data;
  } catch (error) {
    console.error(`❌ 请求失败 (第${retryCount + 1}次):`, error);
    
    if (retryCount < RETRY_CONFIG.maxRetries - 1) {
      console.log(`🔄 ${RETRY_CONFIG.retryDelay}ms后重试...`);
      await new Promise(resolve => setTimeout(resolve, RETRY_CONFIG.retryDelay));
      return fetchWithRetry(url, options, retryCount + 1);
    }
    
    throw error;
  }
};

// 安全的localStorage操作
const safeLocalStorage = {
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      console.log(`💾 保存到localStorage: ${key}`);
    } catch (error) {
      console.error(`❌ 保存到localStorage失败: ${key}`, error);
    }
  },
  
  getItem: (key) => {
    try {
      const value = localStorage.getItem(key);
      console.log(`📖 从localStorage读取: ${key} = ${value}`);
      return value;
    } catch (error) {
      console.error(`❌ 从localStorage读取失败: ${key}`, error);
      return null;
    }
  },
  
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      console.log(`🗑️ 从localStorage删除: ${key}`);
    } catch (error) {
      console.error(`❌ 从localStorage删除失败: ${key}`, error);
    }
  }
};

// 核心授权处理函数
export const processWechatAuth = async (code, formId, state) => {
  // 检查是否已经在处理授权
  if (isAuthProcessing()) {
    console.log('⚠️ 授权正在处理中，等待完成...');
    const success = await waitForAuth();
    return success;
  }
  
  // 设置授权锁
  setAuthLock(true);
  setAuthState(AUTH_STATES.PROCESSING);
  
  // 创建授权Promise
  authPromise = (async () => {
    try {
      // 仅在非生产环境输出调试信息
      if (process.env.NODE_ENV !== 'production') {
        console.log('🚀 开始处理微信授权:', { code, formId, state });
      }
      
      // 检查环境
      if (!isWechatBrowser()) {
        throw new Error('非微信环境，无法进行授权');
      }
      
      // 构建API请求
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';
      
      const requestBody = { code, formId };
      const url = `${API_BASE_URL}/auth/wechat-login`;
      
      console.log('📤 发送授权请求:', { url, requestBody });
      
      // 发送请求（带重试）
      const data = await fetchWithRetry(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });
      
      if (!data.success) {
        throw new Error(data.message || data.error || '微信授权失败');
      }
      
      console.log('✅ 授权成功，保存用户信息');
      
      // 保存用户信息（使用安全的localStorage操作）
      safeLocalStorage.setItem('wechatUser', JSON.stringify(data.user));
      safeLocalStorage.setItem('wechatToken', data.token);
      safeLocalStorage.setItem('isWechatLoggedIn', '1');
      safeLocalStorage.setItem('isLoggedIn', '1');
      
      // 设置授权成功标记
      safeLocalStorage.setItem('wechatAuthCompleted', '1');
      safeLocalStorage.setItem('wechatAuthTimestamp', Date.now().toString());
      
      // 清理跳转标记
      safeLocalStorage.removeItem('isRedirectingToAuth');
      safeLocalStorage.removeItem('isRedirectingToWechat');
      safeLocalStorage.removeItem('isProcessingWechatAuth');
      
      setAuthState(AUTH_STATES.SUCCESS);
      console.log('🎉 微信授权处理完成');
      
      return true;
    } catch (error) {
      console.error('💥 微信授权处理失败:', error);
      setAuthState(AUTH_STATES.FAILED);
      throw error;
    } finally {
      // 解锁
      setAuthLock(false);
    }
  })();
  
  try {
    const result = await authPromise;
    return result;
  } catch (error) {
    console.error('授权处理异常:', error);
    return false;
  }
};

// 获取授权后的跳转URL - 已迁移到 authRedirectManager.js
// 保留此函数以兼容旧代码，实际使用 authRedirectManager.buildAuthRedirectUrl
export const getAuthRedirectUrl = (state) => {
  console.warn('⚠️ getAuthRedirectUrl 已废弃，请使用 authRedirectManager.buildAuthRedirectUrl');
  return buildAuthRedirectUrl(state);
};

// 清理授权相关数据
export const cleanupAuthData = () => {
  try {
    // 清理跳转相关数据
    safeLocalStorage.removeItem('returnUrl');
    safeLocalStorage.removeItem('wechatReturnUrl');
    safeLocalStorage.removeItem('wechatFormId');
    safeLocalStorage.removeItem('fromWechatForm');
    
    // 清理跳转标记
    safeLocalStorage.removeItem('isRedirectingToAuth');
    safeLocalStorage.removeItem('isRedirectingToWechat');
    safeLocalStorage.removeItem('isProcessingWechatAuth');
    
    // 清理授权状态
    clearAuthState();
    
    console.log('🧹 授权数据清理完成');
  } catch (error) {
    console.error('清理授权数据失败:', error);
  }
};

// 检查授权是否成功
export const checkAuthSuccess = () => {
  try {
    const wechatUser = safeLocalStorage.getItem('wechatUser');
    const isLoggedIn = safeLocalStorage.getItem('isWechatLoggedIn') === '1';
    const authCompleted = safeLocalStorage.getItem('wechatAuthCompleted') === '1';
    
    const success = !!(wechatUser && isLoggedIn && authCompleted);
    console.log('🔍 授权状态检查:', { success, wechatUser: !!wechatUser, isLoggedIn, authCompleted });
    
    return success;
  } catch (error) {
    console.error('检查授权状态失败:', error);
    return false;
  }
};

// 防抖处理授权
let authDebounceTimer = null;
export const debouncedAuth = (callback, delay = 300) => {
  if (authDebounceTimer) {
    clearTimeout(authDebounceTimer);
  }
  
  authDebounceTimer = setTimeout(() => {
    callback();
    authDebounceTimer = null;
  }, delay);
};

export default {
  processWechatAuth,
  getAuthRedirectUrl,
  cleanupAuthData,
  checkAuthSuccess,
  getAuthState,
  clearAuthState,
  isAuthProcessing,
  waitForAuth,
  debouncedAuth
};
